import { ensureGsap, prefersReducedMotion } from "@theme/vp-motion";
//#region assets/vp-product-gallery.ts
/**
* PDP media gallery: GSAP slide track with prev/next, swipe, and thumbnails.
*
* Expects markup from `sections/product-details.liquid`.
*/
var SLIDE_DURATION = .45;
var THUMB_DURATION = .35;
var SLIDE_EASE = "power2.out";
var SWIPE_THRESHOLD = 48;
var THUMB_VISIBLE = 3;
var VpProductGallery = class extends HTMLElement {
	/** @type {HTMLElement[]} */
	#slides = [];
	/** @type {HTMLButtonElement[]} */
	#thumbs = [];
	/** @type {HTMLElement | null} */
	#viewport = null;
	/** @type {HTMLElement | null} */
	#track = null;
	/** @type {HTMLElement | null} */
	#thumbsRoot = null;
	/** @type {HTMLElement | null} */
	#thumbsTrack = null;
	/** @type {HTMLElement | null} */
	#thumbsViewport = null;
	/** @type {typeof window.gsap | null} */
	#gsap = null;
	/** @type {gsap.core.Tween | null} */
	#tween = null;
	/** @type {gsap.core.Tween | null} */
	#thumbTween = null;
	/** @type {number} */
	#index = 0;
	/** @type {number} */
	#thumbStart = 0;
	/** @type {boolean} */
	#animating = false;
	/** @type {number | null} */
	#pointerId = null;
	/** @type {number} */
	#startX = 0;
	/** @type {number} */
	#startY = 0;
	/** @type {number} */
	#dragX = 0;
	/** @type {boolean} */
	#tracking = false;
	/** @type {boolean} */
	#swiping = false;
	/** @type {MediaQueryList | null} */
	#mobileMq = null;
	/** @type {() => void} */
	#onResize = () => this.#syncThumbWindow({ instant: true });
	/** @type {() => void} */
	#onThumbScroll = () => this.#updateThumbFade();
	connectedCallback() {
		this.#slides = Array.from(this.querySelectorAll("[data-vp-gallery-slide]"));
		this.#thumbs = Array.from(this.querySelectorAll("[data-vp-gallery-thumb]"));
		this.#viewport = this.querySelector("[data-vp-gallery-viewport]");
		this.#track = this.querySelector("[data-vp-gallery-track]");
		this.#thumbsRoot = this.querySelector("[data-vp-gallery-thumbs]");
		this.#thumbsTrack = this.querySelector("[data-vp-gallery-thumbs-track]");
		this.#thumbsViewport = this.querySelector(".product-details__media-thumbs-viewport");
		this.#mobileMq = window.matchMedia("(max-width: 749px)");
		if (this.#slides.length <= 1 || !this.#track) return;
		this.querySelector("[data-vp-gallery-prev]")?.addEventListener("click", () => this.previous());
		this.querySelector("[data-vp-gallery-next]")?.addEventListener("click", () => this.next());
		this.#thumbs.forEach((thumb) => {
			thumb.addEventListener("click", () => {
				const index = Number(thumb.dataset.index);
				if (Number.isFinite(index)) this.select(index);
			});
		});
		if (this.#viewport) {
			this.#viewport.addEventListener("pointerdown", (event) => this.#onPointerDown(event));
			this.#viewport.addEventListener("pointermove", (event) => this.#onPointerMove(event));
			this.#viewport.addEventListener("pointerup", (event) => this.#onPointerUp(event));
			this.#viewport.addEventListener("pointercancel", (event) => this.#onPointerUp(event));
			this.#viewport.addEventListener("keydown", (event) => this.#onKeydown(event));
			this.#viewport.setAttribute("tabindex", "0");
			this.#viewport.setAttribute("role", "region");
			this.#viewport.setAttribute("aria-roledescription", "carousel");
		}
		this.#thumbsViewport?.addEventListener("scroll", this.#onThumbScroll, { passive: true });
		window.addEventListener("resize", this.#onResize);
		this.#mobileMq?.addEventListener?.("change", this.#onResize);
		ensureGsap().then((gsap) => {
			this.#gsap = gsap;
			this.#goTo(0, { instant: true });
		});
	}
	disconnectedCallback() {
		this.#tween?.kill();
		this.#thumbTween?.kill();
		this.#tween = null;
		this.#thumbTween = null;
		this.#thumbsViewport?.removeEventListener("scroll", this.#onThumbScroll);
		window.removeEventListener("resize", this.#onResize);
		this.#mobileMq?.removeEventListener?.("change", this.#onResize);
	}
	/** @returns {boolean} */
	#isMobileThumbScroll() {
		return Boolean(this.#mobileMq?.matches);
	}
	/**
	* @param {number} index
	* @param {{ instant?: boolean }} [options]
	*/
	select(index, options = {}) {
		this.#goTo(index, options);
	}
	previous() {
		this.#goTo(this.#index - 1);
	}
	next() {
		this.#goTo(this.#index + 1);
	}
	/**
	* @param {number} index
	* @param {{ instant?: boolean }} [options]
	*/
	#goTo(index, options = {}) {
		if (!this.#track || !this.#slides.length) return;
		const nextIndex = (index % this.#slides.length + this.#slides.length) % this.#slides.length;
		const instant = Boolean(options.instant) || prefersReducedMotion();
		const xPercent = -100 * nextIndex;
		this.#index = nextIndex;
		this.#slides.forEach((slide, i) => {
			slide.classList.toggle("is-active", i === nextIndex);
		});
		this.#syncThumbButtons();
		this.#syncThumbWindow({ instant });
		this.#tween?.kill();
		if (!this.#gsap || instant) {
			if (this.#gsap) this.#gsap.set(this.#track, { xPercent });
			else if (this.#track) this.#track.style.transform = `translate3d(${xPercent}%, 0, 0)`;
			this.#animating = false;
			return;
		}
		this.#animating = true;
		this.#tween = this.#gsap.to(this.#track, {
			xPercent,
			duration: SLIDE_DURATION,
			ease: SLIDE_EASE,
			overwrite: true,
			onComplete: () => {
				this.#animating = false;
				this.#tween = null;
			}
		});
	}
	#syncThumbButtons() {
		this.#thumbs.forEach((thumb, i) => {
			const active = i === this.#index;
			thumb.classList.toggle("is-active", active);
			thumb.setAttribute("aria-selected", active ? "true" : "false");
		});
	}
	/**
	* Keep three thumbs in view. Selecting the 3rd+ shifts the strip to reveal more.
	* On mobile, prefer native horizontal scroll so the strip is swipeable.
	* @param {{ instant?: boolean }} [options]
	*/
	#syncThumbWindow(options = {}) {
		if (!this.#thumbsTrack || this.#thumbs.length <= THUMB_VISIBLE) {
			this.#thumbStart = 0;
			if (this.#isMobileThumbScroll()) this.#scrollThumbIntoView(0, { instant: true });
			else this.#setThumbTrackX(0, { instant: true });
			this.#updateThumbFade();
			return;
		}
		const maxStart = Math.max(0, this.#thumbs.length - THUMB_VISIBLE);
		let nextStart = this.#index <= 1 ? 0 : this.#index - 1;
		nextStart = Math.min(Math.max(0, nextStart), maxStart);
		this.#thumbStart = nextStart;
		if (this.#isMobileThumbScroll()) {
			this.#setThumbTrackX(0, { instant: true });
			this.#scrollThumbIntoView(this.#index, options);
		} else if (this.#thumbsViewport) {
			this.#thumbsViewport.scrollLeft = 0;
			this.#setThumbTrackX(this.#thumbOffsetPx(nextStart), options);
		} else this.#setThumbTrackX(this.#thumbOffsetPx(nextStart), options);
		this.#updateThumbFade();
	}
	/**
	* @param {number} index
	* @param {{ instant?: boolean }} [options]
	*/
	#scrollThumbIntoView(index, options = {}) {
		const thumb = this.#thumbs[index];
		const viewport = this.#thumbsViewport;
		if (!thumb || !viewport) return;
		const instant = Boolean(options.instant) || prefersReducedMotion();
		const thumbLeft = thumb.offsetLeft;
		const thumbRight = thumbLeft + thumb.offsetWidth;
		const viewLeft = viewport.scrollLeft;
		const viewRight = viewLeft + viewport.clientWidth;
		let nextLeft = viewLeft;
		if (thumbLeft < viewLeft) nextLeft = thumbLeft;
		else if (thumbRight > viewRight) nextLeft = thumbRight - viewport.clientWidth;
		else return;
		viewport.scrollTo({
			left: Math.max(0, nextLeft),
			behavior: instant ? "auto" : "smooth"
		});
	}
	/**
	* @param {number} start
	* @returns {number}
	*/
	#thumbOffsetPx(start) {
		const thumb = this.#thumbs[0];
		if (!thumb) return 0;
		const styles = getComputedStyle(this.#thumbsTrack);
		const gap = Number.parseFloat(styles.columnGap || styles.gap || "16") || 16;
		return start * (thumb.getBoundingClientRect().width + gap);
	}
	/**
	* @param {number} x
	* @param {{ instant?: boolean }} [options]
	*/
	#setThumbTrackX(x, options = {}) {
		if (!this.#thumbsTrack) return;
		const instant = Boolean(options.instant) || prefersReducedMotion();
		this.#thumbTween?.kill();
		if (!this.#gsap || instant) {
			if (this.#gsap) this.#gsap.set(this.#thumbsTrack, { x: -x });
			else this.#thumbsTrack.style.transform = `translate3d(${-x}px, 0, 0)`;
			this.#thumbTween = null;
			return;
		}
		this.#thumbTween = this.#gsap.to(this.#thumbsTrack, {
			x: -x,
			duration: THUMB_DURATION,
			ease: SLIDE_EASE,
			overwrite: true,
			onComplete: () => {
				this.#thumbTween = null;
			}
		});
	}
	#updateThumbFade() {
		if (!this.#thumbsRoot) return;
		if (this.#isMobileThumbScroll() && this.#thumbsViewport) {
			const { scrollLeft, clientWidth, scrollWidth } = this.#thumbsViewport;
			const canScrollRight = scrollLeft + clientWidth < scrollWidth - 2;
			this.#thumbsRoot.dataset.canScrollRight = canScrollRight ? "true" : "false";
			return;
		}
		const canScrollRight = this.#thumbStart + THUMB_VISIBLE < this.#thumbs.length;
		this.#thumbsRoot.dataset.canScrollRight = canScrollRight ? "true" : "false";
	}
	/**
	* @param {number} offsetPx
	*/
	#setDragOffset(offsetPx) {
		if (!this.#track || !this.#viewport) return;
		const width = this.#viewport.clientWidth || 1;
		const xPercent = -100 * this.#index + offsetPx / width * 100;
		if (this.#gsap) this.#gsap.set(this.#track, { xPercent });
		else this.#track.style.transform = `translate3d(${xPercent}%, 0, 0)`;
	}
	/**
	* @param {PointerEvent} event
	*/
	#onPointerDown(event) {
		if (event.button !== 0 && event.pointerType === "mouse") return;
		if (!(event.target instanceof Element)) return;
		if (event.target.closest("button, a")) return;
		if (this.#animating) return;
		this.#pointerId = event.pointerId;
		this.#startX = event.clientX;
		this.#startY = event.clientY;
		this.#dragX = 0;
		this.#tracking = true;
		this.#swiping = false;
		this.#tween?.kill();
	}
	/**
	* @param {PointerEvent} event
	*/
	#onPointerMove(event) {
		if (!this.#tracking || event.pointerId !== this.#pointerId) return;
		const dx = event.clientX - this.#startX;
		const dy = event.clientY - this.#startY;
		if (!this.#swiping) {
			if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
			if (Math.abs(dy) > Math.abs(dx)) {
				this.#tracking = false;
				this.#pointerId = null;
				this.#goTo(this.#index, { instant: true });
				return;
			}
			this.#swiping = true;
			this.#viewport?.setPointerCapture?.(event.pointerId);
		}
		event.preventDefault();
		this.#dragX = dx;
		this.#setDragOffset(dx);
	}
	/**
	* @param {PointerEvent} event
	*/
	#onPointerUp(event) {
		if (!this.#tracking || event.pointerId !== this.#pointerId) return;
		const dx = this.#dragX;
		this.#tracking = false;
		this.#swiping = false;
		this.#pointerId = null;
		this.#dragX = 0;
		if (this.#viewport?.hasPointerCapture?.(event.pointerId)) this.#viewport.releasePointerCapture(event.pointerId);
		if (Math.abs(dx) >= SWIPE_THRESHOLD) {
			if (dx < 0) this.next();
			else this.previous();
			return;
		}
		this.#goTo(this.#index);
	}
	/**
	* @param {KeyboardEvent} event
	*/
	#onKeydown(event) {
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			this.previous();
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			this.next();
		}
	}
};
if (!customElements.get("vp-product-gallery")) customElements.define("vp-product-gallery", VpProductGallery);
//#endregion
